import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API from '../../api/axios';
import { FiArrowLeft } from 'react-icons/fi';
import { formatDate, formatCreatedAt } from '../../utils/dateUtils';

const InvoiceDetail = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await API.get(`/invoices/${id}`);
        setInvoice(res.data.data.invoice);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  const getStatusBadge = (status) => {
    const map = {
      pending: 'En attente', partial: 'Partiel', paid: 'Payée',
      overdue: 'En retard', cancelled: 'Annulée',
    };
    return <span className={`badge badge-${status}`}>{map[status] || status}</span>;
  };

  const getPaymentStatusBadge = (status) => {
    const map = {
      initiated: 'Initié', pending: 'En cours', success: 'Réussi',
      failed: 'Échoué', cancelled: 'Annulé',
    };
    return <span className={`badge badge-${status}`}>{map[status] || status}</span>;
  };

  const providerNames = {
    mpesa: 'M-Pesa', orange_money: 'Orange Money',
    airtel_money: 'Airtel Money', afrimoney: 'Afrimoney',
  };

  if (loading) {
    return <div className="loading-page"><div className="spinner"></div><p>Chargement...</p></div>;
  }

  if (!invoice) {
    return (
      <div className="empty-state">
        <h3>Facture non trouvée</h3>
        <Link to="/patient/invoices" className="btn btn-primary" style={{ marginTop: 16 }}>
          Retour aux factures
        </Link>
      </div>
    );
  }

  const remaining = parseFloat(invoice.amount) - parseFloat(invoice.amount_paid);

  return (
    <div>
      <Link to="/patient/invoices" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, color: 'var(--gray-600)', fontSize: 14 }}>
        <FiArrowLeft /> Retour aux factures
      </Link>

      <div className="invoice-detail-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        {/* Détails facture */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Facture {invoice.invoice_number}</span>
            {getStatusBadge(invoice.status)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Motif</p>
              <p style={{ fontWeight: 600 }}>{invoice.motif}</p>
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Département</p>
              <p>{invoice.department || '-'}</p>
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Hôpital</p>
              <p>{invoice.hospital_name || '-'}</p>
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Créée par</p>
              <p>{invoice.creator ? `${invoice.creator.first_name} ${invoice.creator.last_name}` : '-'}</p>
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Date de création</p>
              <p>{formatCreatedAt(invoice)}</p>
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Échéance</p>
              <p>{formatDate(invoice.due_date)}</p>
            </div>
          </div>

          {invoice.description && (
            <div style={{ marginTop: 20 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>Description</p>
              <p>{invoice.description}</p>
            </div>
          )}
        </div>

        {/* Résumé montant */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 12 }}>Résumé financier</h3>
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Montant total</p>
              <p style={{ fontSize: 28, fontWeight: 700, color: 'var(--gray-900)' }}>
                {parseFloat(invoice.amount).toLocaleString()} {invoice.currency}
              </p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Déjà payé</p>
              <p style={{ fontSize: 18, fontWeight: 600, color: 'var(--success)' }}>
                {parseFloat(invoice.amount_paid).toLocaleString()} {invoice.currency}
              </p>
            </div>
            <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 12 }}>
              <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Restant à payer</p>
              <p style={{ fontSize: 22, fontWeight: 700, color: remaining > 0 ? 'var(--danger)' : 'var(--success)' }}>
                {remaining.toLocaleString()} {invoice.currency}
              </p>
            </div>
            {remaining > 0 && invoice.status !== 'cancelled' && (
              <Link to={`/patient/pay/${invoice.id}`} className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 16 }}>
                Payer maintenant
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Historique paiements */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header">
            <span className="card-title">Historique des paiements</span>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Montant</th>
                  <th>Fournisseur</th>
                  <th>Téléphone</th>
                  <th>Statut</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {invoice.payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{p.payment_reference}</td>
                    <td style={{ fontWeight: 600 }}>{parseFloat(p.amount).toLocaleString()} {p.currency}</td>
                    <td>{providerNames[p.provider] || p.provider}</td>
                    <td>{p.phone_number}</td>
                    <td>{getPaymentStatusBadge(p.status)}</td>
                    <td>{formatCreatedAt(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;
